import { supabase } from "@/integrations/supabase/client";
import { isForcedReauthRequired } from "./sessionTimeout";

let keepAliveIntervalId: NodeJS.Timeout | null = null;
let lastKeepAliveTime: number | null = null;
let visibilityListener: (() => void) | null = null;

const KEEP_ALIVE_INTERVAL = 11 * 60 * 1000; // 11 minutes

const performKeepAlive = async () => {
  try {
    // Don't run if forced reauth is required
    if (isForcedReauthRequired()) {
      console.log('[AuthKeepAlive] Forced reauth required - skipping keep-alive');
      return;
    }

    // Don't run on suspension/deactivation pages
    const currentPath = window.location.pathname;
    if (currentPath === '/suspended' || currentPath === '/deactivated') {
      console.log('[AuthKeepAlive] On restricted page - skipping keep-alive');
      return;
    }

    // Only run if tab is visible
    if (document.visibilityState !== 'visible') {
      console.log('[AuthKeepAlive] Tab not visible - skipping keep-alive');
      return;
    }

    console.log('[AuthKeepAlive] Performing keep-alive refresh');
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('[AuthKeepAlive] Keep-alive failed:', error);
    } else if (data.session) {
      lastKeepAliveTime = Date.now();
      console.log('[AuthKeepAlive] Keep-alive successful');
    } else {
      console.log('[AuthKeepAlive] No session found during keep-alive');
    }
  } catch (error) {
    console.error('[AuthKeepAlive] Keep-alive error:', error);
  }
};

export const startAuthKeepAlive = () => {
  // Don't start if already running
  if (keepAliveIntervalId) {
    console.log('[AuthKeepAlive] Already running - not starting again');
    return;
  }

  console.log('[AuthKeepAlive] Starting auth keep-alive (11 minute interval)');
  
  // Perform initial keep-alive
  performKeepAlive();
  
  // Set up interval
  keepAliveIntervalId = setInterval(performKeepAlive, KEEP_ALIVE_INTERVAL);
  
  // Set up visibility change listener for immediate refresh on tab focus
  visibilityListener = () => {
    if (document.visibilityState === 'visible') {
      console.log('[AuthKeepAlive] Tab became visible - performing immediate refresh');
      performKeepAlive();
    }
  };
  
  document.addEventListener('visibilitychange', visibilityListener);
};

export const stopAuthKeepAlive = () => {
  console.log('[AuthKeepAlive] Stopping auth keep-alive');
  
  if (keepAliveIntervalId) {
    clearInterval(keepAliveIntervalId);
    keepAliveIntervalId = null;
  }
  
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
  
  lastKeepAliveTime = null;
};

export const getLastKeepAliveTime = () => lastKeepAliveTime;
